import React, { useEffect, useRef, useState } from 'react'
import { shared } from '../../assets/images'
import './style/map.css'
import { AlertDialog } from '../../components/AlertDialog'
import { MapControls } from '../MapControls'
import { Constants } from '../../theme'
import {
  createVehicleMarker,
  getMarkerPosition,
  setMarkerPosition,
  setMarkerIcon,
  setMarkerSelected,
  removeMarker,
  playArrival,
} from './vehicleMarker'

// Los edificios 3D solo existen en mapas vectoriales, que necesitan un Map ID.
// DEMO_MAP_ID es el de pruebas de Google: en producción va el Map ID propio del proyecto.
const MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID'

// Las tres pantallas de mapa cargan Google Maps con las mismas opciones:
// el cargador falla si se llama dos veces con opciones distintas.
export const MAP_LIBRARIES = ['marker']

// La moto se desliza entre coordenadas en vez de saltar. Saltos muy largos
// (reconexión, moto trasladada) se muestran directo para no cruzar medio mapa.
const MOVE_DURATION_MS = 1000
// Si el usuario mueve el mapa, se deja de seguir la moto un rato y luego se vuelve sola.
const FOLLOW_RESUME_MS = 8000
const MAX_ANIMATED_DISTANCE_DEG = 0.02 // ~2 km
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)
// Los datos vienen del servidor: se ponen como texto (textContent), nunca como HTML,
// para que un valor con código no se ejecute en la página.
const vehicleInfoContent = (vehicle) => {
  const content = document.createElement('div')
  content.className = 'trakku-info'
  const brand = document.createElement('strong')
  brand.textContent = vehicle.Marca || ''
  const model = document.createElement('p')
  model.textContent = vehicle.Modelo || ''
  content.append(brand, model)
  return content
}

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Preferencia 3D/2D de quien mira el mapa; si el navegador no deja guardar, se usa 3D.
const VIEW_3D_KEY = 'trakku.map3d'
const readSaved3D = () => {
  try {
    return window.localStorage.getItem(VIEW_3D_KEY) !== '0'
  } catch (error) {
    return true
  }
}
const save3D = (value) => {
  try {
    window.localStorage.setItem(VIEW_3D_KEY, value ? '1' : '0')
  } catch (error) {
    // Sin almacenamiento la elección dura solo mientras la página está abierta.
  }
}

function MapGoogle({
  vehicles,
  view,
  selectedVehicleData,
  userLocation,
  showLocationAlert = true,
  onRecenter,
  recenterLabel,
  recenterImage,
  onLocateUser,
  followVehicleKey,
}) {
  const [count, setCount] = useState(0)
  const [googleMarkers, setGoogleMarkers] = useState(null)
  const [vehiclesLenght, setVehiclesLenght] = useState(
    Object.keys(vehicles || {}).length
  )
  const [is3D, setIs3D] = useState(readSaved3D)
  const [isVector, setIsVector] = useState(false)
  // Marcador avanzado mientras Google acepte el Map ID; si no, marcador clásico.
  const [useAdvancedMarkers, setUseAdvancedMarkers] = useState(true)
  const is3DRef = useRef(is3D)
  is3DRef.current = is3D
  const [showAlert, setShowAlert] = useState(false)
  const [alertDismissed, setAlertDismissed] = useState(false)
  const infoWindow = new google.maps.InfoWindow()
  const mapRef = useRef()
  const markersRef = useRef({})
  const userMarkerRef = useRef(null)
  const moveFramesRef = useRef({})
  // A quién sigue la cámara: la moto ('vehicle') o al usuario ('user'), según el último
  // enfoque (view.follow). Si el usuario arrastra el mapa se pausa y se retoma
  // FOLLOW_RESUME_MS después de soltarlo, sobre lo mismo que venía siguiendo.
  const followModeRef = useRef('vehicle')
  const followPausedRef = useRef(false)
  const resumeFollowTimerRef = useRef(null)
  const userPositionRef = useRef(null)
  const followKeyRef = useRef(followVehicleKey)
  followKeyRef.current = followVehicleKey

  const moveCameraTo = (position) => {
    const map = mapRef.current?.mapInstance
    if (!map) return
    if (map.moveCamera) map.moveCamera({ center: position })
    else map.setCenter(position)
  }

  const moveMarker = (key, marker, to) => {
    cancelAnimationFrame(moveFramesRef.current[key])
    const from = getMarkerPosition(marker) || to
    const follows = () => key === followKeyRef.current
      && followModeRef.current === 'vehicle'
      && !followPausedRef.current
    const distance = Math.max(Math.abs(to.lat - from.lat), Math.abs(to.lng - from.lng))
    if (distance === 0) return
    if (prefersReducedMotion() || distance > MAX_ANIMATED_DISTANCE_DEG) {
      setMarkerPosition(marker, to)
      if (follows()) moveCameraTo(to)
      playArrival(marker)
      return
    }
    const start = performance.now()
    const step = (now) => {
      const progress = Math.min((now - start) / MOVE_DURATION_MS, 1)
      const eased = easeInOutCubic(progress)
      const position = {
        lat: from.lat + (to.lat - from.lat) * eased,
        lng: from.lng + (to.lng - from.lng) * eased,
      }
      setMarkerPosition(marker, position)
      if (follows()) moveCameraTo(position)
      if (progress < 1) {
        moveFramesRef.current[key] = requestAnimationFrame(step)
      } else {
        playArrival(marker)
      }
    }
    moveFramesRef.current[key] = requestAnimationFrame(step)
  }

  // Los edificios tapan algunas vías: con este botón se ve el mapa plano y se vuelve al 3D.
  const toggle3D = () => {
    const next = !is3D
    setIs3D(next)
    save3D(next)
    mapRef.current?.mapInstance?.setTilt(next ? Constants.MAP_BUILDINGS_TILT : 0)
  }

  const zoomBy = (delta) => {
    const map = mapRef.current?.mapInstance
    if (map) map.setZoom(map.getZoom() + delta)
  }

  // Función para cerrar el modal
  const closeAlert = () => {
    setShowAlert(false)
    setAlertDismissed(true)
  }

  const selectedVehicleKey = () =>
    selectedVehicleData?.[0]?.chasis || selectedVehicleData?.[0]?.Chasis

  const updateMarkers = (updatedVehicles) => {
    setCount(count + 1)
    Object.keys(updatedVehicles).forEach((key) => {
      const position = {
        lng: updatedVehicles[key].longitude,
        lat: updatedVehicles[key].latitude,
      }
      const marker = googleMarkers[key]
      if (marker) {
        moveMarker(key, marker, position)
        setMarkerIcon(marker, updatedVehicles[key].img)
        setMarkerSelected(marker, key === selectedVehicleKey())
      }
    })
  }

  const updateUserMarker = () => {
    if (!userLocation && userMarkerRef.current) {
      userMarkerRef.current.setMap(null)
      userMarkerRef.current = null
      return
    }
    if (userLocation && userLocation.latitude && userLocation.longitude) {
      const userPosition = {
        lat: userLocation.latitude,
        lng: userLocation.longitude,
      }
      userPositionRef.current = userPosition

      if (!userMarkerRef.current) {
        userMarkerRef.current = new window.google.maps.Marker({
          position: userPosition,
          map: mapRef.current.mapInstance,
          icon: {
            url: shared.userLocation,
            scaledSize: new window.google.maps.Size(40, 40),
          },
        })

        // Sin autopan: al abrirse, la burbuja movía el mapa hacia el usuario y sacaba la moto de vista.
        const userInfoWindow = new google.maps.InfoWindow({
          disableAutoPan: true,
          content: `
          <div style="text-align: center; font-family: Arial, sans-serif;">
            <p style="margin: 0; font-size: 16px; padding-top: 10px;">¡Tú estás aquí! 😊</p>
          </div>`,
        })

        userInfoWindow.open({
          anchor: userMarkerRef.current,
          map: mapRef.current.mapInstance,
        })
      } else {
        userMarkerRef.current.setPosition(userPosition)
      }
      // Con "Centrar mi ubicación" el mapa sigue al usuario, aunque la moto se mueva.
      if (followModeRef.current === 'user' && !followPausedRef.current) {
        mapRef.current.mapInstance.panTo(userPosition)
      }

      if (showLocationAlert && !alertDismissed) {
        setShowAlert(true)
      }
    }
  }

  useEffect(() => {
    const markers = googleMarkers || {}
    const notEmpty = Object.keys(markers).length > 0
    if (notEmpty) {
      updateMarkers(vehicles)
    }
    setVehiclesLenght(Object.keys(vehicles || {}).length)
  }, [vehicles, selectedVehicleData])

  useEffect(() => {
    const currentMap = mapRef.current.mapInstance
    if (!currentMap) {
      mapRef.current.mapInstance = new window.google.maps.Map(mapRef.current, {
        zoom: view.zoomLevel,
        center: view.center,
        mapId: MAP_ID,
        renderingType: window.google.maps.RenderingType?.VECTOR,
        // Sin botones de Google: se acerca con los dedos o la rueda del mouse.
        disableDefaultUI: true,
        gestureHandling: 'greedy',
        clickableIcons: false,
      })
      const map = mapRef.current.mapInstance
      map.addListener('dragstart', () => {
        followPausedRef.current = true
        clearTimeout(resumeFollowTimerRef.current)
      })
      map.addListener('dragend', () => {
        clearTimeout(resumeFollowTimerRef.current)
        resumeFollowTimerRef.current = setTimeout(() => {
          followPausedRef.current = false
          const marker = markersRef.current[followKeyRef.current]
          const position = followModeRef.current === 'user'
            ? userPositionRef.current
            : marker && getMarkerPosition(marker)
          if (position) map.panTo(position)
        }, FOLLOW_RESUME_MS)
      })
      // El 3D solo existe en mapa vectorial: sin WebGL Google cae a plano y el botón sobra.
      const updateRenderingType = () => setIsVector(map.getRenderingType?.() === 'VECTOR')
      map.addListener('renderingtype_changed', updateRenderingType)
      updateRenderingType()
      const updateMarkerSupport = () => {
        const capabilities = map.getMapCapabilities?.()
        if (capabilities?.isAdvancedMarkersAvailable === false) setUseAdvancedMarkers(false)
      }
      map.addListener('mapcapabilities_changed', updateMarkerSupport)
      updateMarkerSupport()
      return
    }
    // Sin zoom solo se sigue el punto y se respeta el zoom que eligió el usuario.
    if (view.zoomLevel == null) {
      currentMap.panTo(view.center)
      return
    }
    // Enfocar define a quién se sigue: la moto (por defecto) o el usuario (view.follow).
    clearTimeout(resumeFollowTimerRef.current)
    followModeRef.current = view.follow || 'vehicle'
    followPausedRef.current = false
    currentMap.setCenter(view.center)
    currentMap.setZoom(view.zoomLevel)
    if (view.tilt != null) currentMap.setTilt(is3DRef.current ? view.tilt : 0)
  }, [view])

  useEffect(
    () => () => {
      Object.values(moveFramesRef.current).forEach(cancelAnimationFrame)
      clearTimeout(resumeFollowTimerRef.current)
    },
    []
  )

  useEffect(() => {
    const map = mapRef.current.mapInstance
    Object.values(markersRef.current).forEach(removeMarker)

    const markers = Object.keys(vehicles || {}).reduce(
      (reducedMarkers, vehicleKey) => {
        const vehicle = vehicles[vehicleKey]
        const vehicleMarked = createVehicleMarker({
          useAdvanced: useAdvancedMarkers,
          map,
          position: { lng: vehicle.longitude, lat: vehicle.latitude },
          img: vehicle.img,
          selected: vehicleKey === selectedVehicleKey(),
          onClick: () => {
            infoWindow.setContent(vehicleInfoContent(vehicle))
            infoWindow.open({
              anchor: vehicleMarked,
              map,
              shouldFocus: true,
            })
          },
        })

        return { ...reducedMarkers, [vehicleKey]: vehicleMarked }
      },
      {}
    )
    markersRef.current = markers
    setGoogleMarkers(markers)
  }, [vehiclesLenght, useAdvancedMarkers])

  useEffect(() => {
    updateUserMarker()
  }, [userLocation?.latitude, userLocation?.longitude])

  return (
    <>
      {showAlert && (
        <AlertDialog
          openAlert={showAlert}
          handleCloseAlert={closeAlert}
          handleAceptAlert={closeAlert}
          handleCancelAlert={closeAlert}
          title="Ubicación actualizada"
          subtitle="Si ya activaste la ubicación en tiempo real y no ves tu ubicación, intenta recargar la página. Si aún no puedes verla, asegúrate de activar la opción de ubicación en la configuración de tu navegador. Luego, recarga la página para intentarlo nuevamente."
          mainbuttonTitle="Cerrar"
        />
      )}
      <div ref={mapRef} id="map" style={{ width: '100%', height: '100%' }} />
      <MapControls
        onZoomIn={() => zoomBy(1)}
        onZoomOut={() => zoomBy(-1)}
        onRecenter={onRecenter}
        recenterLabel={recenterLabel}
        recenterImage={recenterImage}
        onLocateUser={onLocateUser}
        show3DToggle={isVector}
        is3D={is3D}
        onToggle3D={toggle3D}
      />
    </>
  )
}

export { MapGoogle }
